import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FetchHostPayoutDetails,
  CreatePayout,
  SendAddBankAccountSms,
} from "../../apis/Payout";
import { formatCurrency, formatDate } from "./payoutHelpers";

const HostPayoutDetail = () => {
  const { hostId } = useParams();
  const navigate = useNavigate();

  const [hostData, setHostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReservations, setSelectedReservations] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [viewReservation, setViewReservation] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsCooldownUntil, setSmsCooldownUntil] = useState(() => {
    const stored = localStorage.getItem(
      `sms_cooldown_addBankAccount_${hostId}`,
    );
    if (!stored) return null;
    const until = parseInt(stored, 10);
    if (until > Date.now()) return until;
    localStorage.removeItem(`sms_cooldown_addBankAccount_${hostId}`);
    return null;
  });
  const [smsCountdown, setSmsCountdown] = useState(() => {
    const stored = localStorage.getItem(
      `sms_cooldown_addBankAccount_${hostId}`,
    );
    if (!stored) return 0;
    const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  });

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const res = await FetchHostPayoutDetails(hostId);
      if (res.success) {
        setHostData(res.data);
      } else {
        toast.error("Failed to fetch host payout details.");
      }
    } catch (error) {
      toast.error("Failed to fetch host payout details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostId]);

  const toggleReservation = (resId) => {
    setSelectedReservations((prev) =>
      prev.includes(resId)
        ? prev.filter((id) => id !== resId)
        : [...prev, resId],
    );
  };

  const selectAll = () => {
    if (!hostData?.reservations) return;
    if (selectedReservations.length === hostData.reservations.length) {
      setSelectedReservations([]);
    } else {
      setSelectedReservations(hostData.reservations.map((r) => r._id));
    }
  };

  const selectedSummary = () => {
    if (!hostData?.reservations) return { count: 0, total: 0, fees: 0, net: 0 };
    const selected = hostData.reservations.filter((r) =>
      selectedReservations.includes(r._id),
    );
    return {
      count: selected.length,
      total: selected.reduce((sum, r) => sum + (r.totalPrice || 0), 0),
      fees: selected.reduce((sum, r) => sum + (r.platformFees || 0), 0),
      net: selected.reduce((sum, r) => sum + (r.hostAmount || 0), 0),
    };
  };

  const handleCreatePayout = async () => {
    if (selectedReservations.length === 0) {
      toast.error("Please select at least one reservation.");
      return;
    }
    try {
      setSubmitting(true);
      const res = await CreatePayout({
        hostId,
        reservationIds: selectedReservations,
        notes: notes || undefined,
      });
      if (res.success) {
        toast.success("Payout created successfully!");
        navigate("/payouts/history");
      } else {
        toast.error(res.message || "Failed to create payout.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create payout.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!smsCooldownUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((smsCooldownUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        localStorage.removeItem(`sms_cooldown_addBankAccount_${hostId}`);
        setSmsCooldownUntil(null);
        setSmsCountdown(0);
        clearInterval(interval);
      } else {
        setSmsCountdown(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [smsCooldownUntil]);

  const handleSendAddBankSms = async () => {
    try {
      setSmsSending(true);
      const res = await SendAddBankAccountSms(hostId);
      if (res.success) {
        toast.success("SMS sent to host successfully.");
        const until = Date.now() + 5 * 60 * 1000;
        localStorage.setItem(
          `sms_cooldown_addBankAccount_${hostId}`,
          String(until),
        );
        setSmsCooldownUntil(until);
        setSmsCountdown(300);
      } else {
        toast.error(res.message || "Failed to send SMS.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to send SMS.");
    } finally {
      setSmsSending(false);
    }
  };

  const summary = selectedSummary();

  if (loading) {
    return (
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          </div>
        </Container>
      </div>
    );
  }

  if (!hostData) {
    return (
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6">
            <p>Host data not found.</p>
            <Link to="/payouts" className="text-secondaryColor hover:underline">
              ← Back to Payouts
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  const { host, bankAccount, reservations, summary: hostSummary } = hostData;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <Link
                to="/payouts"
                className="text-secondaryColor hover:underline text-sm"
              >
                ← Back to Payouts
              </Link>
              <h1 className="text-2xl dark:text-[#c7d8eb]">
                Pay Host: {host?.firstName} {host?.lastName}
              </h1>
            </div>

            {/* Host Info & Bank Account */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Host Info */}
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Host Information</h3>
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Name
                    </span>
                    <span>
                      {host?.firstName} {host?.lastName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Email
                    </span>
                    <span>{host?.email}</span>
                  </div>
                  {hostSummary && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Total Reservations
                        </span>
                        <span>{hostSummary.totalReservations}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Total Amount
                        </span>
                        <span>{formatCurrency(hostSummary.totalAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Total Platform Fees
                        </span>
                        <span>
                          {formatCurrency(hostSummary.totalPlatformFees)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Total Host Amount
                        </span>
                        <span className="font-semibold text-green-600">
                          {formatCurrency(hostSummary.totalHostAmount)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Bank Account */}
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Bank Account</h3>
                {bankAccount ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Bank Name
                      </span>
                      <span>{bankAccount.bankName || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        IBAN
                      </span>
                      <span className="font-mono">
                        {bankAccount.IbanNumber || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        Swift Number
                      </span>
                      <span>{bankAccount.swiftNumber || "-"}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-red-500 text-sm mb-3">
                      No bank account on file for this host.
                    </p>
                    <button
                      onClick={handleSendAddBankSms}
                      disabled={smsSending || !!smsCooldownUntil}
                      className={`px-4 py-2 rounded text-white text-sm font-semibold ${
                        smsSending || smsCooldownUntil
                          ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      {smsSending
                        ? "Sending..."
                        : smsCooldownUntil
                          ? `Retry in ${Math.floor(smsCountdown / 60)}m ${smsCountdown % 60}s`
                          : "Send SMS to Add Bank Details"}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Unpaid Reservations */}
            <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">
                  Unpaid Reservations ({reservations?.length || 0})
                </h3>
                <button
                  onClick={selectAll}
                  className="text-secondaryColor text-sm hover:underline"
                >
                  {selectedReservations.length === reservations?.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              {!reservations || reservations.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No unpaid reservations found for this host.
                </p>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-500 dark:border-white">
                          <th className="py-2 px-2 text-center w-[5%]">
                            <input
                              type="checkbox"
                              checked={
                                selectedReservations.length ===
                                reservations.length
                              }
                              onChange={selectAll}
                              className="cursor-pointer"
                            />
                          </th>
                          <th className="py-2 px-2 text-center">Code</th>
                          <th className="py-2 px-2 text-center">Property</th>
                          <th className="py-2 px-2 text-center">Guest</th>
                          <th className="py-2 px-2 text-center">Check-in</th>
                          <th className="py-2 px-2 text-center">Check-out</th>
                          <th className="py-2 px-2 text-center">Total Price</th>
                          <th className="py-2 px-2 text-center">
                            Platform Fees
                          </th>
                          <th className="py-2 px-2 text-center">Host Amount</th>
                          <th className="py-2 px-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reservations.map((reservation) => (
                          <tr
                            key={reservation._id}
                            className={`border-b border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              selectedReservations.includes(reservation._id)
                                ? "bg-blue-50 dark:bg-blue-900/20"
                                : ""
                            }`}
                            onClick={() => toggleReservation(reservation._id)}
                          >
                            <td className="py-2 px-2 text-center">
                              <input
                                type="checkbox"
                                checked={selectedReservations.includes(
                                  reservation._id,
                                )}
                                onChange={() =>
                                  toggleReservation(reservation._id)
                                }
                                className="cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="py-2 px-2 text-center">
                              #{reservation.code}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {reservation.property?.title || "-"}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {reservation.guest?.firstName}{" "}
                              {reservation.guest?.lastName}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {formatDate(reservation.checkIn)}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {formatDate(reservation.checkOut)}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {formatCurrency(reservation.totalPrice)}
                            </td>
                            <td className="py-2 px-2 text-center text-red-500">
                              {formatCurrency(reservation.platformFees)}
                            </td>
                            <td className="py-2 px-2 text-center text-green-600 font-semibold">
                              {formatCurrency(reservation.hostAmount)}
                            </td>
                            <td
                              className="py-2 px-2 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewReservation(reservation);
                                }}
                                className="px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="block lg:hidden">
                    {reservations.map((reservation) => (
                      <div
                        key={reservation._id}
                        className={`mb-4 border border-gray-500 dark:border-white rounded-lg p-3 cursor-pointer ${
                          selectedReservations.includes(reservation._id)
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-400"
                            : ""
                        }`}
                        onClick={() => toggleReservation(reservation._id)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            checked={selectedReservations.includes(
                              reservation._id,
                            )}
                            onChange={() => toggleReservation(reservation._id)}
                            className="cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="font-semibold">
                            #{reservation.code}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500 dark:text-gray-400">
                            Property
                          </span>
                          <span>{reservation.property?.title || "-"}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500 dark:text-gray-400">
                            Guest
                          </span>
                          <span>
                            {reservation.guest?.firstName}{" "}
                            {reservation.guest?.lastName}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500 dark:text-gray-400">
                            Dates
                          </span>
                          <span>
                            {formatDate(reservation.checkIn)} →{" "}
                            {formatDate(reservation.checkOut)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500 dark:text-gray-400">
                            Total
                          </span>
                          <span>{formatCurrency(reservation.totalPrice)}</span>
                        </div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500 dark:text-gray-400">
                            Platform Fees
                          </span>
                          <span className="text-red-500">
                            {formatCurrency(reservation.platformFees)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold">
                          <span className="text-gray-500 dark:text-gray-400">
                            Host Amount
                          </span>
                          <span className="text-green-600">
                            {formatCurrency(reservation.hostAmount)}
                          </span>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewReservation(reservation);
                            }}
                            className="px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded"
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Selected Summary & Create Payout */}
            {reservations && reservations.length > 0 && (
              <div className="bg-[#ffffff] dark:bg-darkBg2 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-3">Payout Summary</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Selected Reservations
                    </div>
                    <div className="text-xl font-bold">{summary.count}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Total Amount
                    </div>
                    <div className="text-xl font-bold">
                      {formatCurrency(summary.total)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Platform Fees
                    </div>
                    <div className="text-xl font-bold text-red-500">
                      {formatCurrency(summary.fees)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Net Payout
                    </div>
                    <div className="text-xl font-bold text-green-600">
                      {formatCurrency(summary.net)}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm text-gray-500 dark:text-gray-400 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md p-2 text-sm dark:bg-darkBg1 dark:text-[#c7d8eb]"
                    rows="2"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes for this payout..."
                  />
                </div>

                <button
                  onClick={handleCreatePayout}
                  disabled={submitting || selectedReservations.length === 0}
                  className={`px-6 py-2 rounded text-white font-semibold text-sm ${
                    submitting || selectedReservations.length === 0
                      ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {submitting ? "Creating Payout..." : "Create Payout"}
                </button>
              </div>
            )}
          </div>
        </Container>
      </div>

      {/* Reservation Detail Modal */}
      {viewReservation && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setViewReservation(null)}
        >
          <div
            className="bg-[#ffffff] dark:bg-darkBg2 dark:text-[#c7d8eb] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-300 dark:border-gray-600">
              <h2 className="text-lg font-semibold">
                Reservation #{viewReservation.code}
              </h2>
              <button
                onClick={() => setViewReservation(null)}
                className="text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 flex flex-col gap-4 text-sm">
              {/* Status Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                  <div className="text-gray-500 dark:text-gray-400 mb-1">
                    Reservation State
                  </div>
                  <span
                    className={`font-semibold capitalize ${
                      viewReservation.reservationState === "completed"
                        ? "text-green-600"
                        : viewReservation.reservationState === "cancelled"
                          ? "text-red-500"
                          : viewReservation.reservationState === "approved"
                            ? "text-blue-500"
                            : "text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {viewReservation.reservationState || "-"}
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                  <div className="text-gray-500 dark:text-gray-400 mb-1">
                    Payment State
                  </div>
                  <span
                    className={`font-semibold capitalize ${
                      viewReservation.paymentState === "completed"
                        ? "text-green-600"
                        : viewReservation.paymentState === "failed"
                          ? "text-red-500"
                          : "text-yellow-500"
                    }`}
                  >
                    {viewReservation.paymentState || "-"}
                  </span>
                </div>
              </div>

              {/* Dates & Guests */}
              <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                <div className="font-semibold mb-2">Stay Details</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Check-in
                    </div>
                    <div>{formatDate(viewReservation.checkIn)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Check-out
                    </div>
                    <div>{formatDate(viewReservation.checkOut)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Guests
                    </div>
                    <div>{viewReservation.guests ?? "-"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Booked On
                    </div>
                    <div>{formatDate(viewReservation.createdAt)}</div>
                  </div>
                </div>
              </div>

              {/* Property */}
              <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                <div className="font-semibold mb-2">Property</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Title
                    </div>
                    <div>{viewReservation.property?.title || "-"}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Currency
                    </div>
                    <div>{viewReservation.property?.currency || "-"}</div>
                  </div>
                  {viewReservation.property?.location?.city && (
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">
                        City
                      </div>
                      <div>
                        {viewReservation.property.location.city.en ||
                          viewReservation.property.location.city.ar ||
                          "-"}
                      </div>
                    </div>
                  )}
                  {viewReservation.property?.location?.country && (
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">
                        Country
                      </div>
                      <div>{viewReservation.property.location.country?.en}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Guest */}
              <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                <div className="font-semibold mb-2">Guest</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Name</div>
                    <div>
                      {viewReservation.guest?.firstName}{" "}
                      {viewReservation.guest?.lastName}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Email
                    </div>
                    <div>{viewReservation.guest?.email || "-"}</div>
                  </div>
                  {viewReservation.guest?.mobileNumber && (
                    <div>
                      <div className="text-gray-500 dark:text-gray-400">
                        Phone
                      </div>
                      <div>{viewReservation.guest.mobileNumber}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                <div className="font-semibold mb-2">Pricing</div>
                {/* Night prices breakdown */}
                {viewReservation.nightsPrices?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-gray-500 dark:text-gray-400 mb-1">
                      Nightly Breakdown
                    </div>
                    {viewReservation.nightsPrices.map((np, i) => (
                      <div
                        key={i}
                        className="flex justify-between text-xs py-0.5"
                      >
                        <span>
                          {np.nbrNights} night{np.nbrNights !== 1 ? "s" : ""} ×{" "}
                          {formatCurrency(np.price)}
                        </span>
                        <span>{formatCurrency(np.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Fees breakdown */}
                {viewReservation.fees?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-gray-500 dark:text-gray-400 mb-1">
                      Fees
                    </div>
                    {viewReservation.fees.map((fee, i) => (
                      <div
                        key={i}
                        className="flex justify-between text-xs py-0.5"
                      >
                        <span className="capitalize">
                          {fee.feeType}
                          {fee.valueType === "percentage" && fee.percentageValue
                            ? ` (${fee.percentageValue}%)`
                            : ""}
                        </span>
                        <span className="text-red-500">
                          {formatCurrency(fee.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Coupon */}
                {viewReservation.coupon?.code && (
                  <div className="flex justify-between text-xs py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">
                      Coupon ({viewReservation.coupon.code})
                    </span>
                    <span className="text-green-600">
                      -{formatCurrency(viewReservation.coupon.discountAmount)}
                    </span>
                  </div>
                )}
                {/* Discount */}
                {viewReservation.discount > 0 && (
                  <div className="flex justify-between text-xs py-0.5">
                    <span className="text-gray-500 dark:text-gray-400">
                      Discount
                    </span>
                    <span className="text-green-600">
                      -{formatCurrency(viewReservation.discount)}
                    </span>
                  </div>
                )}
                <div className="border-t border-gray-300 dark:border-gray-600 mt-2 pt-2">
                  <div className="flex justify-between font-semibold">
                    <span>Total Price</span>
                    <span>{formatCurrency(viewReservation.totalPrice)}</span>
                  </div>
                  <div className="flex justify-between text-red-500">
                    <span>Platform Fees</span>
                    <span>-{formatCurrency(viewReservation.platformFees)}</span>
                  </div>
                  <div className="flex justify-between text-green-600 font-bold">
                    <span>Host Amount</span>
                    <span>{formatCurrency(viewReservation.hostAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Guest Message */}
              {viewReservation.message && (
                <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                  <div className="font-semibold mb-1">Guest Message</div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {viewReservation.message}
                  </p>
                </div>
              )}

              {/* Cancellation / Refund */}
              {(viewReservation.cancelReason ||
                viewReservation.refund !== "notEligible") && (
                <div className="bg-gray-50 dark:bg-darkBg1 rounded p-3">
                  {viewReservation.cancelReason && (
                    <div className="mb-2">
                      <div className="text-gray-500 dark:text-gray-400 mb-1">
                        Cancel Reason
                      </div>
                      <p className="text-red-500">
                        {viewReservation.cancelReason}
                      </p>
                    </div>
                  )}
                  {viewReservation.refund &&
                    viewReservation.refund !== "notEligible" && (
                      <div>
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Refund Status
                        </div>
                        <span
                          className={`capitalize font-semibold ${
                            viewReservation.refund === "completed"
                              ? "text-green-600"
                              : "text-yellow-500"
                          }`}
                        >
                          {viewReservation.refund}
                        </span>
                      </div>
                    )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-4 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setViewReservation(null)}
                className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default HostPayoutDetail;
